import React, { useEffect, useContext, useState, useRef } from 'react';
import { toJS } from 'mobx';
import FadeIn from 'react-fade-in';
import { GlobalHotKeys } from 'react-hotkeys';
import { Observer } from 'mobx-react-lite';
import List, { ItemDragging } from 'devextreme-react/list';
import DataSource from 'devextreme/data/data_source';

import BodyEnd from '../../../../_shared/BodyEnd';
import ConfirmModal from '../../../../_shared/ConfirmModal';
import QuickDrawerHeader from '../../../../_shared/QuickDrawerHeader';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../../../_shared/QuickDrawer';
import RichTextEditor from '../../../../_shared/RichTextEditor';

import ReferralTenantExamDatasetGroupViewStore from '../../../../../../stores/ReferralTenantExamDatasetGroupViewStore';
import QuickDrawerStore from '../../../../../../stores/QuickDrawerStore';

import * as ErrorMessages from '../../../../../../constants/errorMessages';
import * as fn from '../../../../../../utilities/_functions';
import * as oh from '../../../../../../utilities/operationHelper';

import './UpdateReferralTenantExamDatasetGroup.scss';

function UpdateReferralTenantExamDatasetGroup(props) {
    const isMounted = useRef(true);
    const focusTimer = useRef(null);
    const updateReferralGroup = useContext(ReferralTenantExamDatasetGroupViewStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        focusTimer.current = setTimeout(() => {
            quickDrawerFocus(props.drawer);
        }, 100)

        return (() => {
            isMounted.current = false;
            updateReferralGroup.clear();
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        })
    }, []) // eslint-disable-line

    const handleCancel = () => {
        if (fn.isFunction(props.onCancel)) {
            if (updateReferralGroup.hasUnsavedChanges) {
                if (window.confirm(ErrorMessages.DISCARD_CHANGES)) {
                    props.onCancel();
                }
            } else {
                props.onCancel();
            }
        }
    }

    const handleSubmit = event => {
        event.preventDefault();
        updateReferralGroup.save()
            .then(data => {
                if (isMounted.current) {
                    if (props.onSuccess && fn.isFunction(props.onSuccess)) {
                        props.onSuccess(event, data);
                    }
                }
            })
    }

    const handleDelete = () => {
        setConfirmDelete(true);
    }

    const handleConfirmDelete = () => {
        updateReferralGroup.delete()
            .then(() => {
                setConfirmDelete(false);
                handleCancel();
            })
    }

    const handleConfirmDeleteCancel = () => {
        setConfirmDelete(false);
    }

    const handleNameChange = event => {
        updateReferralGroup.data.name = event.target.value;
        updateReferralGroup.hasUnsavedChanges = true;
    }

    const handleDescriptionChange = content => {
        const html = content;
        updateReferralGroup.data.descriptionHtml = ((html === '<p><br></p>') ? null : html);
        updateReferralGroup.hasUnsavedChanges = true;
    }

    const handleExamDatasetReorder = (event) => {
        const { toIndex, fromIndex } = event;
        let tempExamDatasets = [...toJS(updateReferralGroup.selectedDatasets)];

        tempExamDatasets.splice(fromIndex, 1);
        tempExamDatasets.splice(toIndex, 0, toJS(updateReferralGroup.selectedDatasets[fromIndex]));

        for (let i = 0; i < tempExamDatasets.length; i++) {
            tempExamDatasets[i].displayOrder = (i + 1);
        }

        updateReferralGroup.data.examDatasetIds = tempExamDatasets.sort((a, b) => a.displayOrder - b.displayOrder).map(d => { return d.id });
        updateReferralGroup.hasUnsavedChanges = true;
    }

    const handleSelectDataset = event => {
        const extraProps = {
            examPublishedTemplateId: updateReferralGroup.data.examPublishedTemplateId,
            selectedDatasets: toJS(updateReferralGroup.selectedDatasets)
        }
        quickDrawer.activateQuickDrawer('referral', 'exam-data', extraProps, handleAddDatasetSuccess, handleAddDatasetCancel)
            .then(drawer => {
                if (isMounted.current) {
                    focusTimer.current = setTimeout(() => {
                        quickDrawerFocus(drawer);
                    }, 100);
                }
            });
    }

    const handleAddDatasetSuccess = (result) => {
        if (result && result.updated) {
            let tempExamDatasetIds = toJS(updateReferralGroup.data.examDatasetIds);

            tempExamDatasetIds = tempExamDatasetIds.filter(ti => result.data.items.some(i => ti === i.id));
            tempExamDatasetIds = Array.from(new Set([...tempExamDatasetIds, ...result.data.items.map(i => { return i.id })]));

            updateReferralGroup.data.examDatasetIds = tempExamDatasetIds;
            updateReferralGroup.hasUnsavedChanges = true;
        }
    }

    const handleAddDatasetCancel = () => {
    }

    const renderExamDataset = (item, index) => {
        return <>
            <div className={'form-group mb-0' + (index < (updateReferralGroup.selectedDatasets.length - 1) ? ' border-bottom' : '')}>
                <div className='cursor-pointer'>
                    <h4 className='m-0 text-truncate text-truncate-xxl'><strong>{item.label}</strong></h4>
                    <small>{item.key}</small>
                </div>
            </div>
        </>
    }

    return <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) ?
                    <GlobalHotKeys
                        keyMap={{
                            close: ['esc'],
                        }}
                        handlers={{
                            close: event => {
                                handleCancel(event)
                            },
                        }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form onSubmit={handleSubmit}>
            <fieldset disabled={updateReferralGroup.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('exam-dataset', 'default')}
                        action='Update'
                        category='Referral Exam Dataset'
                        onCancel={handleCancel}
                    />
                    <div className='quick-drawer-body'>
                        <Observer>{() =>
                            <>
                                {
                                    updateReferralGroup.isReady ?
                                        <FadeIn>
                                            <div className='update-referral-exam-dataset body-content'>
                                                <section>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-4'>
                                                                <label className='required' htmlFor='update-exam-dataset-name'><small>Name</small></label>
                                                                <Observer>{() =>
                                                                    <input
                                                                        id='update-exam-dataset-name'
                                                                        type='text'
                                                                        maxLength={100}
                                                                        className='form-control'
                                                                        autoComplete={'off'}
                                                                        value={updateReferralGroup.data.name ? updateReferralGroup.data.name : ''}
                                                                        onChange={handleNameChange}
                                                                    />
                                                                }</Observer>
                                                                <small className='text-gray-700'><strong>Important:</strong> Name must be unique across all inputs.</small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='row datasets'>
                                                        <div className='col-12'>
                                                            <label><small>Exam Dataset(s)</small></label>
                                                            <div>
                                                                <List
                                                                    dataSource={
                                                                        new DataSource({
                                                                            key: "id",
                                                                            store: updateReferralGroup.selectedDatasets,
                                                                            paginate: false,
                                                                        })
                                                                    }
                                                                    activeStateEnabled={false}
                                                                    focusStateEnabled={false}
                                                                    hoverStateEnabled={false}
                                                                    itemRender={(data) => { return renderExamDataset(data) }}
                                                                >
                                                                    <ItemDragging
                                                                        allowReordering={true}
                                                                        onReorder={handleExamDatasetReorder}
                                                                    >
                                                                    </ItemDragging>
                                                                </List>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section>
                                                <section>
                                                    {
                                                        showAdvanced ?
                                                            <>
                                                                <FadeIn>
                                                                    <div className='row'>
                                                                        <div className='col-12'>
                                                                            <div className='form-group mb-4'>
                                                                                <label htmlFor='update-exam-dataset-description'><small>Description</small></label>
                                                                                <Observer>{() =>
                                                                                    <RichTextEditor
                                                                                        mode='none'
                                                                                        disableTab={true}
                                                                                        value={updateReferralGroup.data.descriptionHtml ? updateReferralGroup.data.descriptionHtml : ''}
                                                                                        onChange={handleDescriptionChange}
                                                                                    />
                                                                                }</Observer>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </FadeIn>
                                                            </> :
                                                            <div className='mt-4 pt-4 border-top'>
                                                                <button type='button' className='btn btn-link btn-sm p-0' onClick={() => { setShowAdvanced(true) }}>&raquo;	Show advanced options</button>
                                                            </div>
                                                    }
                                                </section>
                                            </div>
                                        </FadeIn> : renderQuickDrawerLoading()
                                }
                            </>
                        }</Observer>
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-4'>
                                <button
                                    type='button'
                                    className='btn btn-light'
                                    onClick={handleSelectDataset}
                                >
                                    Add
                                </button>
                            </div>
                            <div className='col-8'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-danger mr-2'
                                        onClick={handleDelete}
                                    >Delete</button>
                                    <button
                                        type='submit'
                                        className='btn btn-success'
                                    >Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
        <BodyEnd>
            <ConfirmModal
                icon={<i className='fal fa-database text-danger mr-2'></i>}
                message={<>Continue to delete dataset group {updateReferralGroup.data && updateReferralGroup.data.key ? `[${updateReferralGroup.data.key}]` : ''}? </>}
                show={confirmDelete}
                onOption1Click={handleConfirmDelete}
                onCancel={handleConfirmDeleteCancel}
            />
        </BodyEnd>
    </>
}

export default UpdateReferralTenantExamDatasetGroup;